import { Button, DataGrid, TableCell } from '@ketch-com/deck'
import { PermitHistoryDTO } from '@ketch-com/figurehead'
import { Box, Tooltip, Typography } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { ContactMethod, ContactMethodLabels } from 'pages/insights/auditLog/constants'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import { useMemo } from 'react'
import { formatDateTimeFromUnix } from 'utils/helpers/dateTime'

export type HistoricalSubscriptionsContentProps = {
  permitHistory: PermitHistoryDTO[]
}

export const HistoricalSubscriptionsContent: React.FC<HistoricalSubscriptionsContentProps> = ({ permitHistory }) => {
  const { setShowViewExperienceModal, setAuditLogExperiencePreviewPermit } = useAuditLogContext()

  const rowCount = permitHistory.length
  const showPagination = rowCount > 5

  const columns: GridColDef<PermitHistoryDTO>[] = useMemo(
    () => [
      {
        align: 'left',
        field: 'event',
        width: 400,
        headerAlign: 'left',
        headerName: 'Event',
        renderHeader: params => <Typography variant="smallLabel">{params.colDef.headerName}</Typography>,
        sortable: false,
        renderCell: ({ row: permit }: GridRenderCellParams<PermitHistoryDTO>) => {
          const name = permit.permitProperties?.[0]?.topicName || permit.permitProperties?.[0]?.topicCode
          const unsubscribed = !!permit.permitProperties?.every(channel => !channel?.allowed)
          return (
            <TableCell>
              <Box display="flex" flexDirection="column">
                <Typography>
                  {unsubscribed ? 'Unsubscribed from' : 'Subscribed to'} <Typography variant="label">{name}</Typography>
                </Typography>
                <Box display="flex" flexDirection="column">
                  {permit.permitProperties?.map(channel => (
                    <Typography key={`${permit.collectedAt}-${channel.contactMethodCode}`}>
                      <Typography variant="label">
                        {ContactMethodLabels[channel.contactMethodCode as ContactMethod] ||
                          channel.contactMethodCode ||
                          'Unknown Contact Method'}
                      </Typography>{' '}
                      set to <Typography variant="label">{channel?.allowed ? 'Enabled' : 'Disabled'}</Typography>
                    </Typography>
                  ))}
                </Box>
              </Box>
            </TableCell>
          )
        },
      },
      {
        align: 'left',
        field: 'collectedAt',
        width: 200,
        headerAlign: 'left',
        headerName: 'Date & Time',
        renderHeader: params => <Typography variant="smallLabel">{params.colDef.headerName}</Typography>,
        sortable: false,
        renderCell: ({ row: permit }: GridRenderCellParams<PermitHistoryDTO>) => {
          return (
            <TableCell>
              <Typography>{formatDateTimeFromUnix(permit.collectedAt)}</Typography>
            </TableCell>
          )
        },
      },
      {
        align: 'right',
        field: 'viewExperience',
        width: 184,
        renderHeader: () => null,
        sortable: false,
        renderCell: ({ row: permit }: GridRenderCellParams<PermitHistoryDTO>) => {
          const disabled = !permit?.configID
          return (
            <TableCell>
              <Tooltip
                title={
                  disabled
                    ? 'You are unable to view the experience associated with this subscription record. This is either because the subscription was set non-interactively, or it was set before subscription records were associated with experiences.'
                    : ''
                }
                placement="bottom"
              >
                <span>
                  <Button
                    color="secondary"
                    disabled={disabled}
                    onClick={() => {
                      setAuditLogExperiencePreviewPermit(permit || null)
                      setShowViewExperienceModal(true)
                    }}
                    sx={{ minWidth: '132px' }}
                  >
                    View Experience
                  </Button>
                </span>
              </Tooltip>
            </TableCell>
          )
        },
      },
    ],
    [setAuditLogExperiencePreviewPermit, setShowViewExperienceModal],
  )

  return (
    <DataGrid
      autosizeOnMount
      autosizeOptions={{
        expand: true,
        includeHeaders: true,
        includeOutliers: false,
      }}
      getRowHeight={() => 'auto'}
      getRowId={(row: PermitHistoryDTO) => row.collectedAt ?? ''}
      columns={columns}
      rows={permitHistory}
      checkboxSelection={false}
      disableRowSelectionOnClick
      disableColumnMenu
      disableColumnPinning
      disableColumnReorder
      disableColumnResize
      disableChildrenSorting={false}
      disableRowHoverStates
      paginationMode="client"
      rowCount={rowCount}
      pagination={showPagination}
      hideFooter={!showPagination}
      pageSizeOptions={[5, 10, 20]}
      noRowsOverlayProps={{ buttonTitle: '' }}
      sx={{
        '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover': {
          backgroundImage: 'unset',
          cursor: 'auto',
        },
        minWidth: '750px',
      }}
    />
  )
}
